import React, { useMemo } from "react";

import { Flex, Button, Icon, Text, Stack, Image } from "@chakra-ui/react";
import { ArrowRightIcon } from "@heroicons/react/outline";

import useTranslations from "../../../hooks/useTranslations";

import useStore from "../../../store";

import ReportLayout from "../layout/ReportLayout";

import WelcomeReportScreenIllustration from "../../../assets/welcome_report_screen";
import WelcomeHeaderReportScreenIllustration from "../../../assets/welcome_header_report_screen";

export default function WelcomePage() {
  const t = useTranslations();
  const viewer = useStore((state) => state.viewer);
  const sections = useStore((state) => state.report.sections);
  const setCurrentStep = useStore((state) => state.report.setCurrentStep);

  const questionCount = useMemo(
    () => sections.reduce((lhs, rhs) => lhs + rhs.questions.length, 0),
    [sections]
  );

  return (
    <ReportLayout canLeave={false}>
      <Flex flexGrow={1} direction={["column-reverse", "row"]} bgColor='white'>
        <Flex
          direction="column"
          flexGrow={1}
          px={["4", "16", "20", "36"]}
          py={["4", "16"]}
          mb={["32", "0"]}
        >
          <Image
            display={["none", "block"]}
            maxW="100%"
            w={{ base: "0px", md: "100px", "2xl": "150px" }}
            src={WelcomeHeaderReportScreenIllustration}
          />
          <Text
            fontSize={{ base: "2xl", "2xl": "4xl" }}
            fontWeight="bold"
            display={["none", "block"]}
          >
            {t("reports.welcome.welcome")}
          </Text>
          <Text
            fontWeight="medium"
            pt={["1", "2"]}
            fontSize={{ base: "base", "2xl": "2xl" }}
            display={["none", "block"]}
          >
            {t("reports.welcome.invitedToAnswer")}
          </Text>
          <Stack direction="column" spacing="10" pt={["10", "20"]}>
            {[
              t("reports.welcome.answerQuestionNotice"),
              t("reports.welcome.questionCountNotice", {
                questionCount,
                timeExpectedCount: Math.max(
                  1,
                  Math.floor((questionCount * 30) / 60)
                ),
              }),
              t("reports.welcome.autosaveNotice"),
            ].map((text, index) => (
              <Stack direction="row" alignItems="center" spacing="4" key={index}>
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  w={{ base: "8", "2xl": "12" }}
                  h={{ base: "8", "2xl": "12" }}
                  rounded="full"
                  bgColor="brand"
                  color="white"
                  fontSize={{ base: "lg", "2xl": "xl" }}
                  flexShrink={0}
                >
                  {index + 1}
                </Flex>
                <Text
                  fontSize={{ base: "base", "2xl": "xl" }}
                  fontWeight="medium"
                  color="brand"
                  maxW="50ch"
                >
                  {text}
                </Text>
              </Stack>
            ))}
          </Stack>
          <Flex direction="row" mx="auto" display={["block", "none"]} pt="8">
            <Image
              src={viewer.company.visualIdentity}
              objectFit="cover"
              h={["40px", "60px"]}
              alt={viewer.company.name}
            />
          </Flex>
          <Flex
            justifyContent="center"
            alignItems="flex-end"
            py="3"
            bottom={0}
            flexGrow={1}
            w="full"
            bgColor="white"
            position={["fixed", "inherit"]}
            borderTop={["1px", "0px"]}
            borderColor="gray.200"
            pt={["3", "28"]}
            ml={["-4", "0"]}
          >
            <Flex justify="center">
              <Button
                id="start-filling-report"
                size="lg"
                colorScheme="primary"
                onClick={() => setCurrentStep("report")}
                rightIcon={<Icon as={ArrowRightIcon} />}
              >
                {t("reports.welcome.startNow")}
              </Button>
            </Flex>
          </Flex>
        </Flex>
        <Flex
          direction="column"
          minW={[undefined, "40%"]}
          maxW={[undefined, "40%"]}
          flexShrink={0}
          bgColor="primary.50"
          py={["4", "16"]}
          alignItems="center"
        >
          <Image
            maxW="100%"
            w="full"
            px="16"
            src={WelcomeReportScreenIllustration}
          />
          <Text
            fontWeight="semibold"
            pt="8"
            fontSize={{ base: "base", "2xl": "xl" }}
            textAlign="center"
            display={["none", "block"]}
          >
            {t("reports.welcome.howItWorks")}
          </Text>
          <Text
            fontWeight="medium"
            pt="4"
            fontSize={{ base: "base", "2xl": "xl" }}
            textAlign="center"
            px={["8", "16"]}
            maxW="60ch"
            display={["none", "block"]}
          >
            {t("reports.welcome.howItWorkExplanation")}
          </Text>
          <Text
            fontSize={["2xl", "4xl"]}
            fontWeight="bold"
            display={["block", "none"]}
          >
            {t("reports.welcome.welcome")}
          </Text>
          <Text
            textAlign="center"
            fontWeight="medium"
            pt={["1", "2"]}
            fontSize={{ base: "base", "2xl": "xl" }}
            display={["block", "none"]}
            maxW="40ch"
          >
            {t("reports.welcome.invitedToAnswer")}
          </Text>
          <Flex flexGrow={1} alignItems="flex-end">
            <Flex
              direction="row"
              justify="flex-end"
              display={["none", "block"]}
            >
              <Image
                src={viewer.company.visualIdentity}
                objectFit="cover"
                h={["40px", "60px"]}
                alt={viewer.company.name}
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </ReportLayout>
  );
}
